/**
 * BusinessCardStatsPage — admin-only analytics for ONE user's
 * public business card. Embedded in UserCommunicationsPage at
 * /admin/user-communications/:userId/business_card_stats.
 *
 * Layout:
 *   - Sticky range selector (7 / 30 / 90 days)
 *   - 4-up Highlights row (Total Views, Top Buttons, Contact
 *     Submissions, Top Source)
 *   - Per-session activity cards (collapsed: time + city/state +
 *     button-click count; expanded: IP, geo, device, source,
 *     button breakdown, top 3 caps + example_read flag, contact-
 *     submitted flag, pitch watch %, view duration)
 *   - Extras row (avg view duration, first-time vs returning,
 *     device/browser breakdown, peak heatmap, conversion funnel,
 *     pitch watch-through %).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Eye, MousePointerClick, UserPlus, Link2, ChevronDown, ChevronUp, Loader2, MapPin, Smartphone, Globe, Monitor, Clock, Users as UsersIcon, TrendingUp, PlayCircle, BarChart3, Sparkles, Zap, AlertTriangle, BookOpen, Pause, RotateCcw, Download, ExternalLink, Wifi } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { usePageHeader } from '../context/PageHeaderContext';
import { useNavigate } from 'react-router-dom';

const SOURCE_LABELS = {
  email_signature: 'Email Signature',
  orb_text: 'ORB Text',
  copy_link: 'Copy Link',
  text_message: 'Text Message',
  qr_code_scan: 'QR Code Scan',
  unknown: 'Unknown',
  // Legacy aliases — historical events stored under the old keys
  // still render with the same friendly labels.
  email_sig: 'Email Signature',
  sms: 'Text Message',
  qr: 'QR Code Scan',
  direct: 'Unknown',
};

const BUTTON_LABELS = {
  email: 'Email',
  call: 'Call',
  text: 'Text',
  contact: 'Save Contact',
  caps: 'Learn about CAPs',
  pitch: 'Watch the Pitch',
  waitlist: 'Join Waitlist',
  download: 'Download ORB',
};

const fmtMs = (ms) => {
  if (!ms || ms < 1000) return `${ms || 0}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return `${m}m ${rem}s`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return ''; }
};

// Strip protocol + path so the referer reads as a clean origin
// ("instagram.com" rather than the full "https://l.instagram.com/...").
const referrerHost = (raw) => {
  if (!raw) return null;
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).host.replace(/^www\./, '');
    }
    return raw.replace(/^www\./, '').split('/')[0];
  } catch {
    return raw;
  }
};

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="border-0 shadow-sm bg-white dark:bg-[#1a1a1a]" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold leading-tight" data-testid={`${testid}-value`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1 truncate">{sub}</div>}
    </CardContent>
  </Card>
);

const SessionRow = ({ s }) => {
  const [open, setOpen] = useState(false);
  const loc = [s.city, s.region, s.zip].filter(Boolean).join(', ') || (s.country || 'Unknown location');
  const totalClicks = s.total_button_clicks || 0;

  // Per-session conversion tier — same buckets as the global
  // funnel: Submitted > Engaged (read a CAP) > View only.
  let convTier = 'View only';
  let convPct = 33;
  if (s.waitlist_submitted || s.contact_submitted) { convTier = 'Submitted'; convPct = 100; }
  else if ((s.top_caps || []).length > 0) { convTier = 'Engaged (read CAP)'; convPct = 66; }

  // Submission detail label.
  const submissionLabel = s.contact_submitted ? 'Contact info' : (s.waitlist_submitted ? 'Waitlist' : null);

  return (
    <Card className="border border-gray-200 dark:border-[#2a2a2a]" data-testid={`bcs-session-${s.session_id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#222] text-left"
      >
        <MapPin className="w-4 h-4 text-[#586D52] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{loc}</p>
          <p className="text-xs text-muted-foreground truncate">{fmtDate(s.started_at)} · {SOURCE_LABELS[s.source] || s.source}</p>
        </div>
        <span className="text-[11px] font-medium text-[#586D52] bg-[#586D52]/10 px-2 py-0.5 rounded-full flex-shrink-0">{totalClicks} click{totalClicks === 1 ? '' : 's'}</span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-[#2a2a2a] space-y-3 text-xs">
          {/* Headline grid — every per-session metric the admin
              wanted: views, total clicks, source, view duration,
              pitch %, device/browser/os, conversion tier. */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 pt-2">
            <div>
              <span className="text-muted-foreground">Views: </span>
              <span className="font-semibold">1</span>
            </div>
            <div>
              <span className="text-muted-foreground">Buttons clicked: </span>
              <span className="font-semibold">{totalClicks}</span>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Source: </span>
              <span className="font-semibold">{SOURCE_LABELS[s.source] || s.source}</span>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Contact submitted: </span>
              <span className="font-semibold">{submissionLabel ? `Yes — ${submissionLabel}` : 'No'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">View time: </span>
              <span className="font-semibold">{fmtMs(s.view_ms_total)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Pitch watched: </span>
              <span className="font-semibold">{s.pitch_max_pct > 0 ? `${Math.round(s.pitch_max_pct)}%` : '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Device: </span>
              <span className="font-semibold capitalize">{s.device || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Browser: </span>
              <span className="font-semibold">{s.browser || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">OS: </span>
              <span className="font-semibold">{s.os || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">IP: </span>
              <code className="font-mono">{s.ip || '—'}</code>
            </div>
            {/* iter175cg — engagement-velocity + bounce signal. */}
            <div>
              <span className="text-muted-foreground">Time to 1st action: </span>
              <span className="font-semibold">{s.time_to_first_action_ms != null ? fmtMs(s.time_to_first_action_ms) : '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Bounced: </span>
              <span className={`font-semibold ${s.bounced ? 'text-amber-700 dark:text-amber-300' : ''}`}>{s.bounced ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Saved contact (.vcf): </span>
              <span className="font-semibold">{s.vcf_downloaded ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Pitch pauses / replays: </span>
              <span className="font-semibold">{s.pitch_pauses || 0} / {s.pitch_replays || 0}</span>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Referrer: </span>
              <span className="font-semibold">{referrerHost(s.referrer) || '—'}</span>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Visitor timezone: </span>
              <span className="font-semibold">{s.tz || s.ip_tz || '—'}</span>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">ISP / network: </span>
              <span className="font-semibold">{s.isp || '—'}</span>
              {s.as_name ? <span className="text-muted-foreground"> · {s.as_name}</span> : null}
              {s.mobile_carrier ? <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-[#586D52] bg-[#586D52]/10 rounded-full px-1.5 py-0.5"><Wifi className="w-3 h-3" /> mobile</span> : null}
            </div>
            <div className="col-span-2 pt-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-muted-foreground">Conversion: </span>
                <span className="font-semibold text-[#586D52]">{convTier} ({convPct}%)</span>
              </div>
              <div className="h-1.5 bg-gray-100 dark:bg-[#1f1f1f] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#586D52] to-[#7C9B70]" style={{ width: `${convPct}%` }} />
              </div>
            </div>
          </div>

          {/* Buttons clicked breakdown */}
          {Object.keys(s.button_clicks || {}).length > 0 && (
            <div>
              <p className="font-semibold mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">Buttons clicked</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(s.button_clicks).map(([k, n]) => (
                  <span key={k} className="bg-gray-100 dark:bg-[#1f1f1f] rounded-full px-2 py-0.5">
                    {BUTTON_LABELS[k] || k} <span className="font-semibold">×{n}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Top CAPs */}
          {(s.top_caps || []).length > 0 && (
            <div>
              <p className="font-semibold mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">Top CAPs viewed</p>
              <ol className="space-y-1 list-decimal list-inside">
                {s.top_caps.map((c, i) => (
                  <li key={i}>
                    {c.title} <span className="text-muted-foreground">×{c.count}</span>
                    {c.example_read && <span className="ml-1 text-emerald-700 dark:text-emerald-300 font-semibold">· example read</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* iter175cg — Per-CAP wall-clock time spent. */}
          {Object.keys(s.caps_time_ms || {}).length > 0 && (
            <div>
              <p className="font-semibold mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><BookOpen className="w-3 h-3" /> Time spent per CAP</p>
              <ul className="space-y-1">
                {Object.entries(s.caps_time_ms)
                  .sort((a, b) => b[1] - a[1])
                  .map(([title, ms]) => (
                    <li key={title} className="flex items-center justify-between">
                      <span>{title}</span>
                      <span className="font-semibold">{fmtMs(ms)}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {/* iter175cg — Outbound link clicks (mailto / tel / sms /
              external download URL). */}
          {(s.outbound_clicks || []).length > 0 && (
            <div>
              <p className="font-semibold mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Outbound clicks</p>
              <ul className="space-y-1">
                {s.outbound_clicks.map((o, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <span className="truncate">
                      <span className="inline-block px-1.5 py-0.5 rounded bg-gray-100 dark:bg-[#1f1f1f] text-[10px] font-mono mr-1 uppercase">{o.scheme}</span>
                      <span className="truncate">{o.url}</span>
                    </span>
                    <span className="font-semibold flex-shrink-0">×{o.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const BreakdownList = ({ title, data, total, icon: Icon }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, 5);
  if (entries.length === 0) return null;
  return (
    <Card className="border-0 shadow-sm bg-white dark:bg-[#1a1a1a]">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
          <Icon className="w-3.5 h-3.5" /> {title}
        </div>
        <div className="space-y-1.5">
          {entries.map(([k, n]) => {
            const pct = total > 0 ? Math.round((n / total) * 100) : 0;
            return (
              <div key={k}>
                <div className="flex items-center justify-between text-xs">
                  <span className="capitalize">{k}</span>
                  <span className="font-semibold">{n} <span className="text-muted-foreground">({pct}%)</span></span>
                </div>
                <div className="h-1.5 bg-gray-100 dark:bg-[#1f1f1f] rounded-full overflow-hidden">
                  <div className="h-full bg-[#586D52]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const PeakHeatmap = ({ data }) => {
  // data is a 7x24 grid (rows = weekday, cols = hour)
  const max = Math.max(1, ...(data || []).flat());
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return (
    <Card className="border-0 shadow-sm bg-white dark:bg-[#1a1a1a]">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
          <Clock className="w-3.5 h-3.5" /> Views by day & hour
        </div>
        <div className="overflow-x-auto">
          <div className="inline-grid gap-0.5" style={{ gridTemplateColumns: 'auto repeat(24, minmax(10px, 1fr))' }}>
            <div />
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="text-[8px] text-muted-foreground text-center">{h}</div>
            ))}
            {(data || []).map((row, di) => (
              <React.Fragment key={di}>
                <div className="text-[10px] text-muted-foreground pr-1 self-center">{days[di]}</div>
                {row.map((v, hi) => {
                  const intensity = v / max;
                  const bg = v === 0 ? 'rgba(0,0,0,0.04)' : `rgba(88, 109, 82, ${0.15 + intensity * 0.85})`;
                  return <div key={hi} className="aspect-square rounded-sm" style={{ backgroundColor: bg }} title={`${days[di]} ${hi}:00 → ${v} view${v === 1 ? '' : 's'}`} />;
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const FunnelView = ({ funnel }) => {
  const max = Math.max(1, funnel.viewed || 0);
  const steps = [
    { label: 'Viewed card', value: funnel.viewed || 0 },
    { label: 'Read a CAP', value: funnel.viewed_cap || 0 },
    { label: 'Submitted', value: funnel.submitted || 0 },
  ];
  return (
    <Card className="border-0 shadow-sm bg-white dark:bg-[#1a1a1a]">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
          <TrendingUp className="w-3.5 h-3.5" /> Conversion funnel
        </div>
        <div className="space-y-2">
          {steps.map((s, i) => {
            const pct = Math.round((s.value / max) * 100);
            return (
              <div key={i}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span>{s.label}</span>
                  <span className="font-semibold">{s.value} <span className="text-muted-foreground">({max > 0 ? Math.round((s.value / max) * 100) : 0}%)</span></span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-[#1f1f1f] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#586D52] to-[#7C9B70]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Compact tile used in the Overview grid — big enough to fill
// the page without scrolling on a 428×900 mobile viewport.
const MiniTile = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="border-0 shadow-sm bg-white dark:bg-[#1a1a1a] h-full" data-testid={testid}>
    <CardContent className="p-3.5 h-full flex flex-col justify-center">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1.5 truncate">
        {Icon ? <Icon className="w-3.5 h-3.5 shrink-0" /> : null}
        <span className="truncate">{label}</span>
      </div>
      <div className="text-xl font-bold leading-tight truncate" data-testid={`${testid}-value`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1 truncate">{sub}</div>}
    </CardContent>
  </Card>
);

// Compact breakdown tile — used for Top Buttons and Top Sources
// where we still want the top-3 list with bars. Sized to match
// MiniTile.
const BreakdownTile = ({ icon: Icon, label, data, labelMap, total, testid }) => {
  const entries = Object.entries(data || {})
    .map(([k, n]) => [k, n])
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
  const denom = total || entries.reduce((acc, [, n]) => acc + n, 0) || 1;
  return (
    <Card className="border-0 shadow-sm bg-white dark:bg-[#1a1a1a] h-full" data-testid={testid}>
      <CardContent className="p-3.5 h-full flex flex-col">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1.5 truncate">
          {Icon ? <Icon className="w-3.5 h-3.5 shrink-0" /> : null}
          <span className="truncate">{label}</span>
        </div>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">—</p>
        ) : (
          <div className="space-y-1.5">
            {entries.map(([k, n]) => {
              const pct = Math.min(100, Math.round((n / denom) * 100));
              const display = (labelMap && labelMap[k]) || k;
              return (
                <div key={k}>
                  <div className="flex items-center justify-between text-[12px] gap-1">
                    <span className="truncate capitalize">{display}</span>
                    <span className="font-semibold flex-shrink-0">{n}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-[#1f1f1f] rounded-full overflow-hidden">
                    <div className="h-full bg-[#586D52]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Returns the top-1 entry from a `{key: count}` map, or null.
const topEntry = (m) => {
  const arr = Object.entries(m || {}).sort((a, b) => b[1] - a[1]);
  return arr.length > 0 ? { key: arr[0][0], count: arr[0][1] } : null;
};

const BusinessCardStatsPage = ({ api, userId, user, SummarizePopupComponent }) => {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('overview'); // 'overview' | 'details'
  const [summarizeOpen, setSummarizeOpen] = useState(false);

  usePageHeader({
    title: user?.name || 'User',
    back: `/admin/user-communications/${userId}`,
  }, [user?.name, userId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: d } = await api.get(`/admin/card-stats/${userId}`, { params: { days } });
        if (!cancelled) setData(d);
      } catch (e) { /* silent */ } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, userId, days]);

  const headline = data?.headline;
  const extras = data?.extras;

  // Build {button: count} map from headline.most_clicked_buttons
  // for the BreakdownTile.
  const buttonsMap = useMemo(() => {
    const m = {};
    (headline?.most_clicked_buttons || []).forEach((b) => {
      m[b.button] = b.count;
    });
    return m;
  }, [headline]);

  // Restrict source counts to ONLY the canonical sources the user
  // approved, plus the historical aliases so old rows don't get
  // dropped silently (the labels above coalesce them).
  const sourceCounts = useMemo(() => {
    const allowed = new Set([
      'email_signature', 'orb_text', 'copy_link', 'text_message', 'qr_code_scan', 'unknown',
      // legacy aliases
      'email_sig', 'sms', 'qr', 'direct',
    ]);
    const src = headline?.source_counts || {};
    const out = {};
    Object.entries(src).forEach(([k, n]) => { if (allowed.has(k)) out[k] = n; });
    return out;
  }, [headline]);

  const totalViews = headline?.total_views ?? 0;
  const conversionPct = (extras?.funnel?.viewed || 0) > 0
    ? Math.round(((extras?.funnel?.submitted || 0) / extras.funnel.viewed) * 100)
    : 0;

  return (
    <div
      className="fixed inset-x-0 flex flex-col bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] px-3 pt-2"
      style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))' }}
      data-testid="bcs-page"
    >
      {/* Top toolbar: two-prong Overview/Details + range pills.
          Sticky-ish via mb so users always see the active context. */}
      <div className="flex items-center justify-between gap-2 mb-2" data-testid="bcs-toolbar">
        <div className="inline-flex bg-white dark:bg-[#1a1a1a] rounded-full p-0.5 shadow-sm" data-testid="bcs-view-toggle">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'details', label: 'Details' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setView(t.id)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${view === t.id ? 'bg-[#586D52] text-white' : 'text-muted-foreground'}`}
              data-testid={`bcs-view-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 text-[11px]" data-testid="bcs-range">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-full font-semibold ${days === d ? 'bg-[#586D52] text-white' : 'bg-white dark:bg-[#1a1a1a] text-muted-foreground'}`}
              data-testid={`bcs-range-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      )}

      {/* ─────────── Overview ─────────── */}
      {!loading && data && view === 'overview' && (
        <div className="grid grid-cols-4 gap-2 flex-1 min-h-0 pb-2" style={{ gridTemplateRows: 'repeat(3, 1fr) auto' }} data-testid="bcs-overview">
          {/* Row 1 */}
          <MiniTile icon={Eye} label={`Total Views (${days}d)`} value={totalViews} testid="bcs-stat-views" />
          <BreakdownTile icon={MousePointerClick} label="Top Buttons Clicked" data={buttonsMap} labelMap={BUTTON_LABELS} total={Object.values(buttonsMap).reduce((a, b) => a + b, 0)} testid="bcs-stat-buttons" />
          <BreakdownTile icon={Link2} label="Top Sources" data={sourceCounts} labelMap={SOURCE_LABELS} total={totalViews} testid="bcs-stat-sources" />
          <MiniTile icon={Zap} label="Avg Time to 1st Action" value={(extras?.avg_time_to_first_action_ms || 0) > 0 ? fmtMs(extras.avg_time_to_first_action_ms) : '—'} testid="bcs-stat-ttfa" />

          {/* Row 2 */}
          <MiniTile icon={UserPlus} label="Contact Submissions" value={headline?.contact_submissions ?? 0} sub="Waitlist + contact" testid="bcs-stat-submissions" />
          <MiniTile icon={Clock} label="Avg View Duration" value={fmtMs(extras?.avg_view_ms || 0)} testid="bcs-stat-duration" />
          <MiniTile icon={PlayCircle} label="Avg Pitch Watched" value={`${extras?.pitch_avg_pct ?? 0}%`} sub={`${extras?.pitch_completion_rate ?? 0}% completed`} testid="bcs-stat-pitch" />
          <MiniTile icon={AlertTriangle} label="Bounce Rate" value={`${extras?.bounce_rate_pct ?? 0}%`} sub={`${extras?.bounced_sessions ?? 0} bounced`} testid="bcs-stat-bounce" />

          {/* Row 3 — collapse the breakdowns to the top-1 entry
              each (with count) since the user wants a single
              "Top X" headline rather than the full list. */}
          {(() => {
            const dev = topEntry(extras?.device_breakdown);
            const brw = topEntry(extras?.browser_breakdown);
            const os = topEntry(extras?.os_breakdown);
            const topCapTime = extras?.top_cap_by_time;
            return (
              <>
                <MiniTile icon={Smartphone} label="Top Device Type" value={dev ? dev.key : '—'} sub={dev ? `${dev.count} view${dev.count === 1 ? '' : 's'}` : ''} testid="bcs-stat-device" />
                <MiniTile icon={Globe} label="Top Browser" value={brw ? brw.key : '—'} sub={brw ? `${brw.count} view${brw.count === 1 ? '' : 's'}` : ''} testid="bcs-stat-browser" />
                <MiniTile icon={Monitor} label="Top OS" value={os ? os.key : '—'} sub={os ? `${os.count} view${os.count === 1 ? '' : 's'}` : ''} testid="bcs-stat-os" />
                <MiniTile icon={BookOpen} label="Top CAP by Time" value={topCapTime ? topCapTime.cap : '—'} sub={topCapTime ? fmtMs(topCapTime.ms) : ''} testid="bcs-stat-top-cap-time" />
              </>
            );
          })()}
          {/* Row 4 — full-width conversion funnel + percentage */}
          <div className="col-span-4" data-testid="bcs-stat-funnel">
            <Card className="border-0 shadow-sm bg-white dark:bg-[#1a1a1a]">
              <CardContent className="p-3.5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <TrendingUp className="w-3.5 h-3.5" /> Conversion Funnel
                  </div>
                  <div className="text-xl font-bold text-[#586D52]" data-testid="bcs-stat-funnel-pct">{conversionPct}%</div>
                </div>
                <div className="space-y-1.5">
                  {[
                    { label: 'Viewed card', value: extras?.funnel?.viewed || 0 },
                    { label: 'Read a CAP', value: extras?.funnel?.viewed_cap || 0 },
                    { label: 'Submitted', value: extras?.funnel?.submitted || 0 },
                  ].map((s, i) => {
                    const max = Math.max(1, extras?.funnel?.viewed || 0);
                    const pct = Math.round((s.value / max) * 100);
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between text-[12px] mb-0.5">
                          <span className="truncate">{s.label}</span>
                          <span className="font-semibold flex-shrink-0">{s.value} <span className="text-muted-foreground">({max > 0 ? Math.round((s.value / max) * 100) : 0}%)</span></span>
                        </div>
                        <div className="h-2 bg-gray-100 dark:bg-[#1f1f1f] rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-[#586D52] to-[#7C9B70]" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ─────────── Details ─────────── */}
      {!loading && data && view === 'details' && (
        <div className="space-y-2 flex-1 min-h-0 overflow-y-auto pb-2" data-testid="bcs-details">
          {!data.sessions?.length && (
            <p className="text-sm text-muted-foreground py-10 text-center">No card visits yet in this window.</p>
          )}
          <div className="space-y-1.5" data-testid="bcs-sessions">
            {(data.sessions || []).map((s) => (
              <SessionRow key={s.session_id} s={s} />
            ))}
          </div>
        </div>
      )}

      {/* Summarize button — pinned to the bottom of the page,
          opens the existing Summarize popup with kind=business_card
          so the chat-summarizer agent operates on the current
          stats payload. Sticky inside the flex column so it never
          scrolls off the screen. */}
      {!loading && data && (
        <div className="pt-2 pb-2 mt-auto" data-testid="bcs-summarize-bar">
          <Button
            onClick={() => setSummarizeOpen(true)}
            className="w-full h-11 rounded-2xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
            data-testid="bcs-summarize-btn"
          >
            <Sparkles className="w-4 h-4 mr-1" /> Summarize this card
          </Button>
        </div>
      )}

      {/* SummarizePopup is rendered by the parent UserChannelChooser
          flow; we delegate via prop callback. Render a portal-style
          inline popup using the same SummarizePopup wired up in
          UserCommunicationsPage. */}
      {SummarizePopupComponent && (
        <SummarizePopupComponent
          open={summarizeOpen}
          onClose={() => setSummarizeOpen(false)}
          api={api}
          payload={{ user_id: userId, kind: 'business_card' }}
          title="Business Card Stats"
        />
      )}
    </div>
  );
};

export default BusinessCardStatsPage;
